import type { SignInController } from "../SignInController";
import { useAuthSDK } from "~/features/auth-sdk";
import { unformatPhone } from "~/shared/helpers/formatPhone";
import { createCaptcha } from "~/shared/helpers/captcha";
import type { SignInCodeStepController } from "./SignInCodeStepController";
import { snackBarManager } from "~/features/ui-kit";
import dayjs from "dayjs";

const captchaId = 'yandex-captcha-code';

export function useSignInCodeStep(signInController: SignInController, codeStepController: SignInCodeStepController) {
  const { updateSendedOtpData, sendedOtpData, sendOtp, verifyOtp } = useAuthSDK();

  const codeStepForm = codeStepController.form;
  const codeStepRetryForm = codeStepController.retryForm;
  const codeStepOtpError = codeStepController.otpError;

  const formData = codeStepForm.formData;
  const retryFormData = codeStepRetryForm.formData;

  const isRetryLoading = codeStepController.isRetryLoading;
  const allowRetry = codeStepController.allowRetry;

  watch(sendedOtpData, (payload) => {
    allowRetry.value = false;
    
    if (!payload) {
      return;
    }

    const diff = dayjs(payload.retryAfter).diff(dayjs(), 'seconds');

    setTimeout(() => {
      allowRetry.value = true;
    }, diff * 1000);
  }, { immediate: true, deep: true });

  const isCodeStep = computed(() => signInController.isCodeStep);
  const isCodeStepFormValid = computed(() => codeStepForm.validate());
  const isRetryFormValid = computed(() => codeStepRetryForm.validate());
  const retryAfter = computed(() => {
    return new Date(sendedOtpData.value?.retryAfter || 0);
  });

  const goToCodeStep = signInController.goToCodeStep.bind(signInController);


  function renderCaptcha() {
    createCaptcha(captchaId, (token: string) => {
      retryFormData.captcha.value = token;
    });
  }

  function handleRetryCodeError(error: unknown) {
    const errorMessage = codeStepController.getRetryErrorMessage(error);

    snackBarManager.showSnackBar({
      type: 'error',
      message: errorMessage,
      dismissible: true,
      duration: 5000,
    });

    renderCaptcha();
    codeStepRetryForm.reset();
  }

  async function retryCode() {
    try {
      isRetryLoading.value = true;
      await sendOtp(unformatPhone(sendedOtpData.value?.phone || ''), retryFormData.captcha.value || '');
    } catch (error) {
      handleRetryCodeError(error);
    } finally {
      isRetryLoading.value = false;
      updateSendedOtpData();
    }
  }

  async function handleSubmitCodeError(error: unknown) {
    const errorMessage = await codeStepController.getSubmitCodeErrorMessage(error);

    if (errorMessage) {
      snackBarManager.showSnackBar({
        type: 'error',
        message: errorMessage,
        dismissible: true,
        duration: 5000,
      });
    }
    
    console.error(error);
    codeStepOtpError.value = 'Неверный код';
    codeStepForm.reset();
  }

  async function submitCode(code: string) {
    try {
      const response = await verifyOtp(code);
      
      if (!response.isRegistered) {
        signInController.goToFillingProfileStep();
      } else {
        signInController.closeSignInPopup();
        signInController.resetStep();
      }
    } catch (error) {
      handleSubmitCodeError(error);
    }
  }

  return {
    isCodeStep,
    isCodeStepFormValid,
    isRetryLoading,
    allowRetry,
    isRetryFormValid,
    
    sendedOtpData,
    formData,
    retryAfter,
    
    codeStepForm,
    codeStepRetryForm,
    codeStepOtpError,
    
    goToCodeStep,
    submitCode,
    retryCode,
    renderCaptcha,
  }
}